import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { printLabelEntry, toastLabelsReady } from "@/components/BulkLabelReadyTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Scale, Printer, ScanLine } from "lucide-react";

// Manual weight entry for Bulk Data parcels still missing a weight (incl. no-tracking rows).
export const ManualWeightDialog = ({ row, onClose, onSaved }) => {
  const [vals, setVals] = useState({});
  const [fillAll, setFillAll] = useState("");
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanConf, setScanConf] = useState({});
  useEffect(() => { setVals({}); setFillAll(""); setScanConf({}); }, [row?.id]);
  if (!row) return null;
  const list = row.missing_weight || [];
  const filled = list.filter(p => Number(vals[p.row]) > 0).length;

  const save = async (andPrint) => {
    const weights = list.filter(p => Number(vals[p.row]) > 0).map(p => ({ row: p.row, weight: Number(vals[p.row]) }));
    if (!weights.length) { toast.error("Enter at least one weight"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/bulk-label/ready/${row.id}/weights`, { weights }, { headers: getAuthHeader() });
      toast.success(`Saved ${r.data.saved} weight(s)`);
      const auto = r.data.auto_label;
      if (auto) {
        localStorage.setItem(`auto-label-seen-${auto.id}`, "1");
        if (andPrint) printLabelEntry(auto.id).catch(() => toast.error("Print failed"));
        else toastLabelsReady(auto, row.filename);
      }
      onSaved(andPrint && !auto);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save weights"); }
    setSaving(false);
  };

  const scan = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append("files", f));
    setScanning(true);
    try {
      const r = await axios.post(`${API}/bulk-label/ready/${row.id}/scan-weights`, fd, { headers: getAuthHeader(), timeout: 180000 });
      const m = r.data.matches || [];
      setVals(v => { const n = { ...v }; m.forEach(x => { n[x.row] = String(x.weight); }); return n; });
      setScanConf(c => { const n = { ...c }; m.forEach(x => { n[x.row] = x.confidence; }); return n; });
      if (m.length) toast.success(`Filled ${m.length} weight(s) from the scan — check amber ones before saving`);
      else toast.warning("No weights on the scan matched these parcels");
      if (r.data.unmatched > 0) toast.info(`${r.data.unmatched} reading(s) did not match a parcel missing weight`);
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    setScanning(false);
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bl-manual-weight-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-amber-600" /> Enter missing weights</DialogTitle>
          <DialogDescription>{row.filename} · {row.invoice_no || "no invoice"} — weights in grams. Saved into the Bulk Data sheet (and Database A for tracked parcels), then used on the labels.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Same weight for all empty:</span>
          <Input type="number" min="1" value={fillAll} onChange={e => setFillAll(e.target.value)} className="h-7 w-24 text-xs" data-testid="bl-mw-fill-all-input" />
          <Button size="sm" variant="outline" className="h-7" data-testid="bl-mw-fill-all-btn"
            onClick={() => Number(fillAll) > 0 && setVals(v => { const n = { ...v }; list.forEach(p => { if (!(Number(n[p.row]) > 0)) n[p.row] = fillAll; }); return n; })}>Apply</Button>
          <label className={`ml-2 inline-flex items-center gap-1 h-7 px-2 rounded border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 cursor-pointer ${scanning ? "opacity-60 pointer-events-none" : ""}`}
            title="Upload a photo/PDF of the filled-in weight sheet — AI reads the handwritten weights" data-testid="bl-mw-scan-btn">
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
            {scanning ? "Reading…" : "Scan weight sheet"}
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="bl-mw-scan-input"
              onChange={e => { scan(e.target.files); e.target.value = ""; }} />
          </label>
          <span className="ml-auto text-slate-500" data-testid="bl-mw-count">{filled}/{list.length} filled</span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500">
              <th className="py-1.5 px-2">#</th><th className="px-2">Receiver</th><th className="px-2">Tracking</th><th className="px-2 w-28">Weight (g)</th>
            </tr></thead>
            <tbody>
              {list.map((p, i) => (
                <tr key={p.row} className="border-t" data-testid={`bl-mw-row-${p.row}`}>
                  <td className="py-1 px-2 text-slate-400">{i + 1}</td>
                  <td className="px-2 font-medium">{p.name || "—"}</td>
                  <td className="px-2 font-mono">{p.tracking || <span className="rounded bg-amber-100 text-amber-800 px-1.5 text-[10px] font-semibold">No tracking</span>}</td>
                  <td className="px-2 py-1">
                    <Input type="number" min="1" value={vals[p.row] ?? ""} autoFocus={i === 0}
                      onChange={e => setVals(v => ({ ...v, [p.row]: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") { const nx = document.querySelector(`[data-testid="bl-mw-input-${list[i + 1]?.row}"]`); nx?.focus(); } }}
                      className={`h-7 text-xs ${scanConf[p.row] && scanConf[p.row] !== "high" ? "border-amber-400 bg-amber-50" : scanConf[p.row] ? "border-emerald-300" : ""}`}
                      title={scanConf[p.row] ? `From scan (${scanConf[p.row]} confidence)` : ""} data-testid={`bl-mw-input-${p.row}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bl-mw-cancel">Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving || !filled} data-testid="bl-mw-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save weights
          </Button>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => save(true)} disabled={saving || !filled} data-testid="bl-mw-save-print">
            <Printer className="w-4 h-4 mr-1" /> Save &amp; Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
